import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VITALIS_LOGO = "https://vitalis-sports.ch/wp-content/uploads/2024/12/logo_web.png";

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { t, language, setLanguage } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success(t('resetEmailSent'));
    } catch (err) {
      // Always show success to prevent email enumeration
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Hero Section */}
      <div 
        className="login-hero hidden md:block"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1200&q=80')`
        }}
      >
        <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white">
          <div>
            <img src={VITALIS_LOGO} alt="Vitalis Sports" className="h-12" />
          </div>
          <div className="max-w-md">
            <h1 className="text-4xl mb-4 text-white">
              {language === 'de' ? 'PASSWORT ZURÜCKSETZEN' : 
               language === 'en' ? 'RESET YOUR PASSWORD' : 
               'RÉINITIALISER VOTRE MOT DE PASSE'}
            </h1>
            <p className="text-white/80">
              {language === 'de' ? 'Wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts.' :
               language === 'en' ? 'We will send you a link to reset your password.' :
               'Nous vous enverrons un lien pour réinitialiser votre mot de passe.'}
            </p>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="md:hidden flex justify-center mb-8">
            <img src={VITALIS_LOGO} alt="Vitalis Sports" className="h-12" />
          </div>

          {/* Language Selector */}
          <div className="flex justify-center gap-2 mb-8">
            {['fr', 'en', 'de'].map((lang) => (
              <button
                key={lang}
                onClick={() => setLanguage(lang)}
                className={`lang-btn ${language === lang ? 'active' : ''}`}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>

          {!sent ? (
            <>
              <div className="mb-8">
                <h2 className="text-3xl mb-2">{t('forgotPassword')}</h2>
                <p className="text-muted-foreground text-sm">
                  {t('forgotPasswordDescription')}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="forgot-email-input"
                    placeholder="email@vitalis-sports.ch"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="forgot-submit-btn"
                  disabled={loading}
                  className="w-full"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  {loading ? t('loading') : t('sendResetLink')}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h2 className="text-2xl mb-2">{t('emailSent')}</h2>
                <p className="text-muted-foreground text-sm">
                  {t('checkYourEmail')}
                </p>
              </div>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              data-testid="back-to-login"
              className="text-sm text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
